import { Check, LampDesk, Mic } from "@tamagui/lucide-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  getGetStatusOfEncounterAssociatedWithCuraiVisitQueryKey,
  useGetClinicStatusForPatient,
  useGetClinicianWaitTime,
} from "@/api/generated";
import { EncounterStartType } from "@/api/generated/models";
import videoCallImage from "@/assets/images/video-call.png";
import {
  BulletItem,
  BulletedList,
  CloseButton,
  Header,
  Heading,
  Image,
  Paragraph,
  ScrollView,
  Section,
  Text,
  YStack,
} from "@/components";
import { RotationWrapper, withRotation } from "@/components/animated";
import Ellipses from "@/components/animated/Ellipses";
import TextTransitionAnimation from "@/components/animated/TextTransitionAnimation";
import Logo from "@/components/Logo";
import SyncIcon from "@/components/SyncIcon";
import { shortBrandName } from "@/config/brand";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";

const DEFAULT_WAIT_TIME_COPY = "3-5 min average wait time";

const LIST_ITEMS = [
  "Keeping all your info private and HIPAA-compliant",
  "Sending your subjective info safely and securely",
  "Updating your chart so your clinician has the full picture",
  "Matching you with the right clinician for your needs",
];

const VIDEO_PREP_ITEMS = [
  {
    text: "Find a quiet, well-lit space where you can speak comfortably.",
    icon: <LampDesk size="$300" flexShrink={0} />,
  },
  {
    text: "You'll need to enable your camera and microphone to continue.",
    icon: <Mic size="$300" flexShrink={0} />,
  },
];

type WaitingRoomProps = {
  onClose: () => void;
  encounterType: EncounterStartType;
  curaiConversationId: string;
};

export default function WaitingRoom({
  onClose,
  encounterType,
  curaiConversationId,
}: WaitingRoomProps) {
  const queryClient = useQueryClient();
  const router = usePropagatedRouter();
  const { data: waitTime } = useGetClinicianWaitTime();
  const waitTimeMinutes = waitTime?.estimated_wait_minutes ?? 0;
  const waitTimeCopy =
    waitTimeMinutes > 0
      ? `~${waitTimeMinutes} min wait time`
      : DEFAULT_WAIT_TIME_COPY;
  const { data: clinicStatus } = useGetClinicStatusForPatient({
    query: {
      refetchOnWindowFocus: true,
      refetchInterval: 1000 * 30,
      refetchOnReconnect: true,
      refetchIntervalInBackground: true,
    },
  });

  useEffect(() => {
    if (clinicStatus?.latest_visit?.status === "in-progress") {
      // Visit has been taken, likely the socket update was missed due to backgrounded tab or otherwise
      queryClient.invalidateQueries({
        queryKey:
          getGetStatusOfEncounterAssociatedWithCuraiVisitQueryKey(
            curaiConversationId,
          ),
        exact: false,
      });
      router.navigate(`/conversation/${curaiConversationId}`);
      onClose();
    }
  }, [clinicStatus, curaiConversationId, onClose, queryClient, router]);

  const isVideoEncounter = encounterType === EncounterStartType.AUDIOVISUAL;

  return (
    <YStack height="100%" borderRadius="$150" backgroundColor="$fillModal">
      <Header
        flexDirection="row"
        justifyContent="center"
        marginBottom="$200"
        marginHorizontal="$200"
        marginTop="$300"
      >
        <Logo.MedicalGroup
          height="$200"
          $md={{ height: "$250" }}
          $lg={{ height: "$300" }}
          color="$textPrimary"
        />
        <CloseButton
          position="absolute"
          right={0}
          onClose={onClose}
          dataTrackingId="waiting-room-close"
        />
      </Header>
      <Section gap="$100" paddingHorizontal="$200" marginBottom="$300">
        <Heading size="$lg">
          Connecting you to a clinician
          <Ellipses />
        </Heading>
        <Text size="$sm" color="$textSecondary">
          {waitTimeCopy}
        </Text>
      </Section>

      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingBottom: "$300",
          flex: 1,
          gap: "$100",
          paddingHorizontal: "$200",
        }}
      >
        {isVideoEncounter ? (
          <YStack
            gap="$300"
            marginBottom="auto"
            width="100%"
            $lg={{ maxWidth: 400, alignSelf: "center", flex: 1 }}
          >
            <YStack width="100%">
              <Image
                source={videoCallImage}
                width="100%"
                height="auto"
                aspectRatio={16 / 9}
                objectFit="cover"
                borderTopLeftRadius="$150"
                borderTopRightRadius="$150"
                overflow="hidden"
              />

              <YStack
                backgroundColor="$fillCallout"
                padding="$300"
                gap="$200"
                borderBottomWidth={1}
                borderLeftWidth={1}
                borderRightWidth={1}
                borderColor="$strokeNeutral"
                borderBottomLeftRadius="$150"
                borderBottomRightRadius="$150"
                overflow="hidden"
              >
                <YStack gap="$100">
                  <Heading size="$md">Prepare for your video visit</Heading>
                  <Paragraph size="$sm" color="$textSecondary">
                    Since it's your first time with us, this will be a video
                    visit to get you set up.
                  </Paragraph>
                </YStack>

                {VIDEO_PREP_ITEMS.map((item) => (
                  <BulletItem
                    key={item.text}
                    itemText={item.text}
                    icon={item.icon}
                    color="$textSecondary"
                    size="$sm"
                  />
                ))}
              </YStack>
            </YStack>

            <YStack width="100%" $lg={{ marginTop: "auto" }}>
              <TextTransitionAnimation
                isVisible
                texts={LIST_ITEMS}
                iconProps={{ color: "$textSecondary" }}
                icon={withRotation(SyncIcon, { reverse: true })}
              />
            </YStack>
          </YStack>
        ) : (
          <>
            <BulletedList.Animated
              id={curaiConversationId}
              items={LIST_ITEMS}
              icon={
                <RotationWrapper reverse>
                  <SyncIcon size="$300" flexShrink={0} />
                </RotationWrapper>
              }
              doneIcon={<Check size="$300" flexShrink={0} />}
              rootProps={{
                columnGap: "$200",
                rowGap: "$300",
                marginBottom: "auto",
              }}
            />

            <Text size="$xs" lineHeight="$sm" color="$textSecondary" italic>
              {`You can continue using ${shortBrandName} while your telehealth visit session remains active. Your place in line is secure, and you can return to the chat at any time.`}
            </Text>
          </>
        )}
      </ScrollView>
    </YStack>
  );
}
